// src/pages/outsourcing_management/InquiryDialogLayout.tsx
//
// The shape of the inquiry dialog, in one place so both halves of it are the
// same shape. Raising an inquiry and reading one are the same record at two
// moments of its life, and they used to be laid out as if they were not: the
// create form drew two columns of its own, the detail view three, so pressing
// "New Inquiry" and opening a row gave two different screens.
//
// It lives outside InquiryDialog because both InquiryDialog and
// InquiryCreateForm need it, and InquiryDialog already imports the create
// form.
import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFull from "@mui/icons-material/OpenInFull";
import { EMAIL_WIDTH } from "components/Inquiry/EmailPreviewPanel";

type ColumnsPropsType = {
  /** The inquiry itself: its fields, and its item lines under them. */
  left: ReactNode;
  /**
   * The supplier email. A document, so it gets a column rather than a rail.
   * Left out, the column goes with it and the item lines take the room -
   * from Pickup / Delivery on they carry the goods-in counts and need it.
   */
  middle?: ReactNode;
  /** What can be done with the inquiry, and what it is attached to. */
  right: ReactNode;
  /**
   * The button that opens `middle` in a dialog of its own, on a dialog too
   * narrow to show it as a column.
   */
  middleLabel?: string;
};

// The middle column is the email's width, not a share of the dialog's. The
// email is a document authored at a fixed width (EMAIL_WIDTH), and a column
// sized as a fraction of the dialog is a column that is nearly always the
// wrong width for it - too narrow and the preview is a scaled-down picture of
// the email, too wide and it floats in whitespace. It is built to fit the
// email instead, and the other two columns divide what is left.
//
// A flex child's default min-width is auto - its own min-content width, which
// beats any max-width it is given. That is how the email used to widen its
// column and take the room from the actions beside it, so every column here
// pins minWidth: 0 and clips.
// Below md the columns each take a whole line, the way they did as xs={12}
// Grid items. The email is behind its button long before that.
const COLUMN = { minWidth: 0, overflow: "hidden" };

// The flex bases below, and the gap between columns (theme spacing 3).
const LEFT_BASIS = 380;
const RAIL_BASIS = 340;
const GAP = 24;
// The narrowest row that holds all three columns at their bases. Anything
// narrower and the email goes behind a button: the actions and the history
// are used on every visit, the email only now and then, so the email is the
// one that gives way rather than the rail wrapping under the other two.
const THREE_COLUMNS = LEFT_BASIS + EMAIL_WIDTH + RAIL_BASIS + 2 * GAP;

const DialogColumns: FunctionComponent<ColumnsPropsType> = ({
  left,
  middle,
  right,
  middleLabel = "Email preview",
}) => {
  const [row, setRow] = useState<HTMLDivElement | null>(null);
  // 0 until measured, which counts as wide enough - the common screen - so a
  // wide dialog does not open with a button that then turns into a column.
  const [rowWidth, setRowWidth] = useState(0);
  const [middleOpen, setMiddleOpen] = useState(false);

  useEffect(() => {
    if (!row) return;
    setRowWidth(row.clientWidth);
    // A frame later, not inside the callback: the width decides whether the
    // email column exists, and changing the layout within the observer's own
    // notification is the "ResizeObserver loop" error.
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setRowWidth(entry.contentRect.width)
      );
    });
    observer.observe(row);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [row]);

  const middleInline = !rowWidth || rowWidth >= THREE_COLUMNS;

  return (
    <Box
      ref={setRow}
      data-testid="inquiry-columns"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        // Stretch, so the email column is as tall as whatever is beside it and
        // the preview inside it can fill that height instead of guessing one.
        alignItems: "stretch",
        gap: 3,
      }}
    >
      {/* Takes most of what the email leaves: it holds the form and the item
        lines, which are the widest thing here after the email. Twice the
        rail's share, not three times - at 3 the rail held at its basis on
        every screen that fitted all three columns, and the history wrapped
        into a tall, thin strip. */}
      <Box
        data-testid="inquiry-column-left"
        sx={{ ...COLUMN, flex: { xs: "1 1 100%", md: `2 1 ${LEFT_BASIS}px` } }}
      >
        {left}
      </Box>
      {middle && middleInline && (
        <Box
          data-testid="inquiry-column-middle"
          sx={{
            ...COLUMN,
            // Shrinks rather than wrapping alone on a dialog too narrow to hold
            // all three columns; the preview scales down with it.
            flex: { xs: "1 1 100%", md: `0 1 ${EMAIL_WIDTH}px` },
            // A column of its own, so the preview can take what the heading
            // above it leaves rather than being given a height by hand.
            display: "flex",
            flexDirection: "column",
          }}
        >
          {middle}
        </Box>
      )}
      <Box
        data-testid="inquiry-column-rail"
        sx={{
          ...COLUMN,
          // 340px is what a history entry needs to read on one or two lines.
          // A dialog too narrow for that beside the email puts the email
          // behind a button (THREE_COLUMNS) rather than squeezing the rail.
          flex: { xs: "1 1 100%", md: `1 1 ${RAIL_BASIS}px` },
          // A rail, not a document: it takes the height the other two columns
          // set and never adds to it. Size containment makes its own content
          // count for nothing when the row's height is worked out - the
          // history alone can run to dozens of rows, and the dialog used to
          // grow to fit them. What does not fit scrolls, inside the column;
          // the detail view gives that scroll to the history alone (see
          // RailScroll). Off on one-column layouts, where the height of the
          // row would be its own content.
          contain: { md: "size" },
          minHeight: { md: 360 },
          display: "flex",
          flexDirection: "column",
          overflowY: { md: "auto" },
        }}
      >
        {middle && !middleInline && (
          <Box sx={{ flexShrink: 0, mb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<OpenInFull fontSize="small" />}
              onClick={() => setMiddleOpen(true)}
            >
              {middleLabel}
            </Button>
          </Box>
        )}
        {right}
      </Box>
      {middle && !middleInline && (
        <Dialog
          data-testid="inquiry-middle-dialog"
          open={middleOpen}
          onClose={() => setMiddleOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <IconButton
            onClick={() => setMiddleOpen(false)}
            size="small"
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {/* The same content the column would hold, heading and all, in a
            box of a fixed height - the preview fills what the heading
            leaves, as it does in the column. */}
          <DialogContent
            sx={{ height: "80vh", display: "flex", flexDirection: "column" }}
          >
            {middle}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

/**
 * The item tables are wider than the column they sit in - they carry a
 * preview, a blueprint menu and a spec. Scroll the table rather than the page.
 * Room underneath, so the last line does not sit flush on whatever follows
 * (EE-253).
 */
export const ItemLinesScroll: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => <div style={{ overflowX: "auto", paddingBottom: 16 }}>{children}</div>;

/**
 * The part of the right column that gives way. Wrap the history in it and the
 * actions above stay put while the history scrolls in what height is left;
 * below md, where the column is as tall as it likes, it is capped instead.
 */
export const RailScroll: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      flex: "1 1 auto",
      minHeight: 0,
      overflowY: "auto",
      maxHeight: { xs: 400, md: "none" },
    }}
  >
    {children}
  </Box>
);

export default DialogColumns;
